import { hasOpenDialog } from '@client/helpers/dom.helpers';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DragValidity,
  getDragValidity,
  hasImportableExtension,
} from './csv-import.helpers';

export type CsvDropState =
  | { kind: 'idle' }
  | { kind: 'dragging'; validity: Exclude<DragValidity, 'idle'> }
  | { kind: 'importing'; filename: string };

type UseCsvFileDropOptions = {
  onImport: (file: File) => Promise<void>;
  onInvalidFile: (file: File) => void;
};

const IDLE_STATE: CsvDropState = { kind: 'idle' };

const isFileDrag = (event: DragEvent): boolean =>
  Boolean(event.dataTransfer?.types.includes('Files'));

/**
 * Attaches window-level drag/drop listeners and tracks the current drop-zone
 * state. Uses a drag counter to ignore `dragenter`/`dragleave` bubbling from
 * descendant elements so the overlay does not flicker when the cursor moves
 * between children.
 *
 * Drops that land inside a `[role="dialog"]` element are ignored so dialog-owned
 * dropzones (EPD PDF import, JSON project import, …) can handle their own files
 * without the build-view overlay flashing or stealing the drop.
 *
 * @param onImport - invoked with a valid `.csv` {@link File} after drop; the
 *   hook transitions to `importing` state for the duration of the returned
 *   promise and returns to `idle` on settle.
 * @param onInvalidFile - invoked when the dropped file fails extension
 *   validation; callers typically surface a toast.
 */
export const useCsvFileDrop = ({
  onImport,
  onInvalidFile,
}: UseCsvFileDropOptions): { dragState: CsvDropState } => {
  const [dragState, setDragState] = useState<CsvDropState>(IDLE_STATE);
  const importRef = useRef(onImport);
  const invalidRef = useRef(onInvalidFile);
  importRef.current = onImport;
  invalidRef.current = onInvalidFile;

  const handleFile = useCallback(async (file: File) => {
    if (!hasImportableExtension(file.name)) {
      invalidRef.current(file);
      setDragState(IDLE_STATE);
      return;
    }
    setDragState({ kind: 'importing', filename: file.name });
    try {
      await importRef.current(file);
    } finally {
      setDragState(IDLE_STATE);
    }
  }, []);

  useEffect(() => {
    let counter = 0;

    const reset = () => {
      counter = 0;
      setDragState((prev) => (prev.kind === 'dragging' ? IDLE_STATE : prev));
    };

    const onDragEnter = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      counter += 1;
      // Dialogs (EPD PDF import, JSON project import, …) own their own dropzone.
      if (hasOpenDialog()) return;
      const validity = getDragValidity(event.dataTransfer);
      if (validity === 'idle') return;
      setDragState((prev) =>
        prev.kind === 'importing' ? prev : { kind: 'dragging', validity },
      );
    };

    const onDragOver = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      if (hasOpenDialog()) return;
      event.preventDefault();
    };

    const onDragLeave = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      counter -= 1;
      if (counter <= 0) reset();
    };

    const onDrop = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      counter = 0;
      if (hasOpenDialog()) {
        setDragState(IDLE_STATE);
        return;
      }
      const file = event.dataTransfer?.files[0];
      if (!file) {
        setDragState(IDLE_STATE);
        return;
      }
      void handleFile(file);
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);

    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [handleFile]);

  return { dragState };
};
